import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Printer, ArrowLeft } from "@phosphor-icons/react";

export default function Labels() {
  const { batch_no } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [mrp, setMrp] = useState(299);
  const [labelsPerPage, setLabelsPerPage] = useState(24);
  const [count, setCount] = useState(24);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/trace/${batch_no}`);
        setData(data);
        // If packed_qty known, default count
        if (data?.batch?.packed_qty) setCount(data.batch.packed_qty);
      } catch (e) {}
    })();
  }, [batch_no]);

  if (!data) return <div className="p-8 text-muted-foreground">Loading label sheet…</div>;

  const b = data.batch;
  const s = data.sku || {};
  const mfg = b.mfg_date ? new Date(b.mfg_date) : new Date();
  const exp = b.exp_date ? new Date(b.exp_date) : new Date(Date.now() + 365*86400e3);
  const fmt = (d) => d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  const labels = Array.from({ length: count });

  return (
    <div className="min-h-screen bg-background">
      {/* Print controls (hidden when printing) */}
      <div className="no-print sticky top-0 z-30 border-b border-border bg-card px-6 py-4 flex flex-wrap items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => nav(-1)} data-testid="labels-back"><ArrowLeft size={14} className="mr-1"/> Back</Button>
        <div className="flex-1">
          <div className="overline">Print Label Sheet</div>
          <div className="font-display font-bold text-lg mono">{b.batch_no} · {b.sku_name}</div>
        </div>
        <div className="flex items-end gap-3">
          <div className="w-24">
            <Label className="overline">MRP ₹</Label>
            <Input type="number" value={mrp} onChange={e=>setMrp(e.target.value)} data-testid="labels-mrp"/>
          </div>
          <div className="w-24">
            <Label className="overline">Labels</Label>
            <Input type="number" value={count} onChange={e=>setCount(parseInt(e.target.value)||1)} data-testid="labels-count"/>
          </div>
          <Button onClick={()=>window.print()} data-testid="labels-print">
            <Printer size={16} className="mr-1.5"/> Print
          </Button>
        </div>
      </div>

      {/* Printable sheet — A4, 3 cols × 8 rows = 24 per page */}
      <div className="print-sheet p-8 print:p-0">
        <div className="grid grid-cols-3 gap-3 print:gap-2">
          {labels.map((_, i) => (
            <div key={i} className="label border border-border rounded-md p-3 bg-card break-inside-avoid">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">Chericious</div>
                  <div className="font-display font-black text-[13px] leading-tight tracking-tight truncate">{b.sku_name}</div>
                  <div className="mono text-[10px] text-muted-foreground">{b.sku_code}</div>
                </div>
                <QRCodeCanvas
                  value={JSON.stringify({batch:b.batch_no, sku:b.sku_code, mfg:b.mfg_date, exp:b.exp_date})}
                  size={62}
                  fgColor="#0A0A0A"
                  includeMargin={false}
                />
              </div>
              <div className="grid grid-cols-2 gap-x-2 gap-y-0.5 text-[9px] font-mono">
                <div><span className="text-muted-foreground">MFG</span> {fmt(mfg)}</div>
                <div><span className="text-muted-foreground">EXP</span> {fmt(exp)}</div>
                <div><span className="text-muted-foreground">NET</span> {s.net_weight_g || 50}g</div>
                <div><span className="text-muted-foreground">BATCH</span> {b.batch_no}</div>
              </div>
              <div className="border-t border-border mt-2 pt-1.5 flex justify-between items-center">
                <div className="text-[9px] text-muted-foreground">Freeze-Dried · Storage: Cool & Dry</div>
                <div className="font-display font-black text-sm text-primary">₹{mrp}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body, html { background: white !important; }
          .print-sheet { padding: 8mm !important; }
          .label { break-inside: avoid; page-break-inside: avoid; }
          @page { size: A4; margin: 8mm; }
        }
      `}</style>
    </div>
  );
}
